import { useState, useEffect, useCallback, useRef } from "react";
import debounce from "lodash.debounce";

const useServerSideSearch = (
  apiFunction,
  searchKeys = [],
  debounceDelay = 500,
  customResponseMapper = null, // ✅ NEW: Custom response mapper
  initialFilters = {} // ✅ allow initial filters to be supplied so initial fetch can use them
) => {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ---------------------------
  // 🔥 Main fetch function
  // ---------------------------
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = {
        pageNumber: currentPage,
        perPage: pageSize, // backend expects `perPage` (mapped to PageSize)
        SearchText: searchText || "",
        ...filters,
      };

      // Helpful debug when investigating initial/refresh fetch issues
      console.debug("useServerSideSearch fetching with params:", params);

      const response = await apiFunction(params);

      // ✅ Use custom mapper if provided, otherwise use default mapping
      const mappedData = customResponseMapper
        ? customResponseMapper(response)
        : {
            data: response.items || response.data || response || [],
            totalCount: response.totalCount || response.length || 0,
            stats: response.summary || {},
          };

      setData(mappedData.data);
      setTotalRows(mappedData.totalCount);
      setStats(mappedData.stats);
    } catch (error) {
      console.error("Server search error:", error);
      setData([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    apiFunction,
    currentPage,
    pageSize,
    searchText,
    filters,
    customResponseMapper,
  ]);

  // ---------------------------
  // 🔥 Debounced Search
  // ---------------------------
  const debouncedSearch = useCallback(
    debounce((text) => {
      setSearchText(text);
      setCurrentPage(1);
    }, debounceDelay),
    []
  );

  const debounceRef = useRef(null);

  const handleSearch = (value) => {
    setSearchText(value);

    // Clear old timeout
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Wait after user stops typing
    debounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchData();
    }, debounceDelay);
  };

  // ---------------------------
  // 🔥 Filters
  // ---------------------------
  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
    setCurrentPage(1);
  };

  // ---------------------------
  // 🔥 Pagination
  // ---------------------------
  const handlePaginationChange = ({ page, perPage }) => {
    setCurrentPage(page);
    if (perPage) setPageSize(perPage);
  };

  // ---------------------------
  // 🔥 Auto-fetch on state change
  // ---------------------------
  useEffect(() => {
    fetchData();
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debouncedSearch.cancel();
    };
  }, [currentPage, pageSize, searchText, filters]);

  return {
    data,
    loading,
    stats,
    totalRows,
    searchQuery: searchText,
    filters,
    currentPage,
    pageSize,

    handleSearch,
    handleFilterChange,
    handlePaginationChange,

    refetch: fetchData,
  };
};

export default useServerSideSearch;
